/**
 * Shrink a photo in the browser before it is uploaded: long edge at most 1920 px, WebP (JPEG where the browser
 * cannot write WebP), EXIF rotation applied. A phone photo goes from ~2–5 MB to ~150–400 KB — it looks the same
 * on any screen, pages open faster and storage grows ten times slower.
 * Anything that is not a JPEG/PNG/WebP, is already small, or does not get smaller is uploaded as it is.
 */
const MAX_EDGE = 1920;
const QUALITY = 0.82;
const SMALL_ENOUGH = 300 * 1024;
const SHRINKABLE = ['image/jpeg', 'image/png', 'image/webp'];

async function decode(file: File): Promise<{ img: CanvasImageSource; w: number; h: number; close: () => void }> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { img: bmp, w: bmp.width, h: bmp.height, close: () => bmp.close() };
  }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;
  await img.decode();
  return { img, w: img.naturalWidth, h: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
}

const toBlob = (canvas: HTMLCanvasElement, type: string) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, QUALITY));

export async function compressImage(file: File): Promise<File> {
  if (!SHRINKABLE.includes(file.type)) return file;
  try {
    const { img, w, h, close } = await decode(file);
    try {
      const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
      if (scale === 1 && file.size <= SMALL_ENOUGH) return file;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      let blob = await toBlob(canvas, 'image/webp');
      // old Safari writes PNG when asked for WebP — a photo then goes to JPEG (a PNG keeps its transparency as PNG)
      if (!blob || blob.type !== 'image/webp') blob = await toBlob(canvas, file.type === 'image/png' ? 'image/png' : 'image/jpeg');
      if (!blob || blob.size >= file.size) return file;
      const ext = blob.type === 'image/webp' ? 'webp' : blob.type === 'image/png' ? 'png' : 'jpg';
      const name = file.name.replace(/\.[^.]+$/, '') + '.' + ext;
      return new File([blob], name, { type: blob.type, lastModified: file.lastModified });
    } finally {
      close();
    }
  } catch {
    return file; // an image the browser cannot read is uploaded untouched
  }
}
